import { Anchor, Button, Group, NumberInput, Select, Table } from '@mantine/core';
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import { IconAdjustments, IconPlus } from '@tabler/icons-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, podatki } from '../api/odjemalec';
import { datum } from '../oblika/oblika';
import { DaNe } from '../seznam/Filtri';
import { GlavaZaslona, IskalnoPolje, Seznam, Stolpec, ZaslonSeznama } from '../seznam/Seznam';
import { daNeZaApi, useSeznam, type OsnovaSeznama } from '../seznam/useSeznam';
import { Oznaka } from '../strani/PregledRacunov';
import { stanjeVozila } from './vozilo';
import { usePravica } from '../seja/pravice';

/** Ključi razvrščanja (VozilaKontroler.pregled). */
type Razvrsti = 'znamka' | 'tip' | 'tipMotorja' | 'vin' | 'regSt' | 'nakup';

const SIRINA = { base: 'calc(50% - 6px)', sm: 160 };

/**
 * Pregled vozil: iskanje, znamka → tip → barva (izbire s strežnika glede na izbrano),
 * prodano, posredniška, zaloga, financirano pri leasingu (novo v spletu) in zaslužek nad/pod mejo; razvrščanje in strani na strežniku, stanje v URL-ju; stolpec
 * z odprtimi povpraševanji z javne ponudbe (novo v spletu). Na
 * telefonu so filtri razen iskanja skriti za gumbom »Filtri«. Klik na vrstico odpre podrobnosti, »Dodaj vozilo« nov vnos.
 */
export function PregledVozil() {
  const ureja = usePravica('VOZILA', 'UREJANJE');
  const seznam = useSeznam<Razvrsti>();
  const navigate = useNavigate();
  const siroko = useMediaQuery('(min-width: 48em)');
  const [filtriOdprti, setFiltriOdprti] = useState(false);
  const p = (ime: string) => seznam.parameter(ime) ?? undefined;
  const zasluzek = Number(p('zasluzek') ?? 0);
  const filtri = {
    iskanje: seznam.iskanje || undefined,
    znamka: p('znamka'),
    tip: p('tip'),
    barva: p('barva'),
    prodano: daNeZaApi(seznam.parameter('prodano')),
    posredniska: daNeZaApi(seznam.parameter('posredniska')),
    zaloga: daNeZaApi(seznam.parameter('zaloga')),
    financirano: daNeZaApi(seznam.parameter('financirano')),
    zasluzek,
    meja: zasluzek ? Number(p('meja') ?? 0) || 0 : undefined,
  };
  const poizvedba = useQuery({
    queryKey: ['vozila', filtri, seznam.zaApi],
    queryFn: ({ signal }) =>
      podatki(api.GET('/api/vozila/pregled', { signal, params: { query: { ...filtri, ...seznam.zaApi } } })),
    placeholderData: keepPreviousData,
  });
  const vrstice = poizvedba.data?.vsebina ?? [];
  const steviloFiltrov = [
    'znamka',
    'tip',
    'barva',
    'prodano',
    'posredniska',
    'zaloga',
    'financirano',
    'zasluzek',
  ].filter((k) => seznam.parameter(k)).length;

  return (
    <ZaslonSeznama>
      <GlavaZaslona
        naslov="Vozila"
        opis="Pregled vozil na zalogi in prodanih vozil."
        dejanja={
          ureja && (
            <Button component={Link} to="/vozila/novo" leftSection={<IconPlus size={18} />}>
              Dodaj vozilo
            </Button>
          )
        }
      />
      <Seznam
        seznam={seznam}
        filtri={
          <>
            <IskalnoPolje seznam={seznam} namig="Znamka, tip, VIN, reg. št. …" />
            <Button
              hiddenFrom="sm"
              variant="default"
              leftSection={<IconAdjustments size={18} />}
              onClick={() => setFiltriOdprti((o) => !o)}
              aria-expanded={filtriOdprti}
            >
              Filtri{steviloFiltrov ? ` (${steviloFiltrov})` : ''}
            </Button>
            {(siroko || filtriOdprti) && <FiltriVozil seznam={seznam} />}
          </>
        }
        glava={
          <>
            <Stolpec seznam={seznam} kljuc="znamka" naziv="Znamka" />
            <Stolpec seznam={seznam} kljuc="tip" naziv="Tip" />
            <Stolpec seznam={seznam} kljuc="tipMotorja" naziv="Tip motorja" />
            <Stolpec seznam={seznam} kljuc="vin" naziv="VIN" />
            <Stolpec seznam={seznam} kljuc="regSt" naziv="Reg. št." />
            <Stolpec seznam={seznam} kljuc="nakup" naziv="Nakup" />
            <Stolpec seznam={seznam} naziv="Povpraševanja" />
          </>
        }
        vrstice={vrstice.map((v, i) => {
          const stanje = stanjeVozila(v);
          return (
            <Table.Tr key={v.id} style={{ cursor: 'pointer' }} onClick={() => void navigate(`/vozila/${v.id}`)}>
              <Table.Td c="dimmed" className="brez-preloma">
                {seznam.zamik + i + 1}
              </Table.Td>
              <Table.Td>
                <Group gap={6} wrap="nowrap">
                  <Anchor
                    component={Link}
                    to={`/vozila/${v.id}`}
                    onClick={(e) => e.stopPropagation()}
                    fw={600}
                    className="brez-preloma"
                  >
                    {v.znamka}
                  </Anchor>
                  {stanje && <Oznaka barva={stanje.barva}>{stanje.naziv}</Oznaka>}
                  {v.posredniskaId ? <Oznaka barva="grape">posredniška</Oznaka> : null}
                </Group>
              </Table.Td>
              <Table.Td>{v.tip}</Table.Td>
              <Table.Td>{v.tipMotorja}</Table.Td>
              <Table.Td className="brez-preloma">{v.vin}</Table.Td>
              <Table.Td className="brez-preloma">{v.regSt}</Table.Td>
              <Table.Td className="brez-preloma">{datum(v.datumNabave)}</Table.Td>
              <Table.Td className="brez-preloma">
                {v.povprasevanja ? (
                  <Oznaka barva={v.novaPovprasevanja ? 'red' : 'yellow'}>
                    {v.novaPovprasevanja ? `${v.novaPovprasevanja} novo` : `${v.povprasevanja} odprto`}
                  </Oznaka>
                ) : null}
              </Table.Td>
            </Table.Tr>
          );
        })}
        stVrstic={vrstice.length}
        stStolpcev={7}
        skupaj={poizvedba.data?.skupaj ?? 0}
        nalaganje={poizvedba.isPending}
        osvezevanje={poizvedba.isFetching}
        napaka={poizvedba.error}
        ponovi={() => void poizvedba.refetch()}
        prazno="Ni vozil za izbrane pogoje."
        napakaNaslov="Vozil ni bilo mogoče naložiti"
      />
    </ZaslonSeznama>
  );
}

/** Znamka → tip → barva, da/ne filtri in zaslužek. */
function FiltriVozil({ seznam }: { seznam: OsnovaSeznama }) {
  const znamka = seznam.parameter('znamka') ?? '';
  const tip = seznam.parameter('tip') ?? '';
  const znamke = useQuery({
    queryKey: ['vozila-znamke'],
    queryFn: ({ signal }) => podatki(api.GET('/api/vozila/znamke', { signal })),
  });
  const tipi = useQuery({
    queryKey: ['vozila-tipi', znamka],
    enabled: !!znamka,
    queryFn: ({ signal }) => podatki(api.GET('/api/vozila/tipi', { signal, params: { query: { znamka } } })),
  });
  const barve = useQuery({
    queryKey: ['vozila-barve', znamka, tip],
    queryFn: ({ signal }) =>
      podatki(
        api.GET('/api/vozila/barve', {
          signal,
          params: { query: { znamka: znamka || undefined, tip: tip || undefined } },
        }),
      ),
  });
  const izbira = (oznaka: string, ime: string, data: string[] | undefined, ponastavi: string[] = [], vse = 'Vse') => (
    <Select
      label={oznaka}
      placeholder={vse}
      data={[...new Set((data ?? []).filter(Boolean))]}
      value={seznam.parameter(ime)}
      onChange={(v) => seznam.nastavi({ [ime]: v, ...Object.fromEntries(ponastavi.map((k) => [k, null])) })}
      searchable
      clearable
      w={SIRINA}
      comboboxProps={{ width: 240, position: 'bottom-start' }}
    />
  );
  const zasluzek = seznam.parameter('zasluzek');
  // zaslužek = prodana − nabavna cena: samo s pravico za nabavne cene (strežnik filter sicer prezre)
  const nabavne = usePravica('NABAVNE_CENE');

  return (
    <>
      {izbira('Znamka', 'znamka', znamke.data, ['tip', 'barva'])}
      {izbira('Tip', 'tip', znamka ? tipi.data : [], ['barva'], znamka ? 'Vsi' : 'Najprej znamka')}
      {izbira('Barva', 'barva', barve.data)}
      <DaNe seznam={seznam} ime="prodano" oznaka="Prodano" />
      <DaNe seznam={seznam} ime="posredniska" oznaka="Posredniška" />
      <DaNe seznam={seznam} ime="zaloga" oznaka="Zaloga" />
      <DaNe seznam={seznam} ime="financirano" oznaka="Financirano (leasing)" />
      {nabavne && (
        <Select
          label="Zaslužek"
          data={[
            { value: '0', label: 'Vsi' },
            { value: '1', label: 'Večji od' },
            { value: '2', label: 'Manjši od' },
          ]}
          value={zasluzek ?? '0'}
          onChange={(v) => seznam.nastavi({ zasluzek: v === '0' ? null : v })}
          allowDeselect={false}
          w={SIRINA}
        />
      )}
      {nabavne && zasluzek && <MejaZasluzka seznam={seznam} />}
    </>
  );
}

/** Meja zaslužka v evrih: počaka, da uporabnik neha tipkati. */
function MejaZasluzka({ seznam }: { seznam: OsnovaSeznama }) {
  const [vnos, setVnos] = useState<string | number>(seznam.parameter('meja') ?? 0);
  const [zakasnjen] = useDebouncedValue(vnos, 500);
  useEffect(() => {
    const v = String(zakasnjen || '');
    if (v !== (seznam.parameter('meja') ?? '')) {
      seznam.nastavi({ meja: v && v !== '0' ? v : null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- samo ob spremembi vnosa
  }, [zakasnjen]);
  return (
    <NumberInput
      label="Meja (€)"
      value={vnos}
      onChange={setVnos}
      min={0}
      allowDecimal={false}
      thousandSeparator="."
      decimalSeparator=","
      w={SIRINA}
    />
  );
}
